import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  LinkOutlined,
  RocketOutlined,
  StarFilled,
  StarOutlined,
  SwapRightOutlined,
  TagsOutlined,
} from '@ant-design/icons'
import { useToggle } from 'ahooks'
import { Button, Dropdown, Menu, Popconfirm, Tag, Tooltip, message } from 'antd'
import copy from 'copy-to-clipboard'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Group, PostTagType, PostType, RequestedPostFragment, usePostTagUpdateMutation } from '@/generated/graphql'
import { useIsSystemAdmin } from '@/modules/admin/admin.hooks'
import { useIsGroupAdmin } from '@/modules/group/group.hooks'
import { AnnouncementPostModal } from '@/modules/post/announcement-post/announcement-post-modal.component'
import { EventPostModal } from '@/modules/post/event-post/event-post-modal.component'
import { HonorPostModal } from '@/modules/post/honor-post/honor-post-modal.component'
import { PostEditor } from '@/modules/post/post-editor'
import {
  useArchivePost,
  useFeaturePost,
  usePostFavorToggle,
  useTopFeatured,
  useTopPost,
} from '@/modules/post/post.hooks'
import { ThanksMessageModal } from '@/modules/post/thanks-post/thanks-message.component'
import { routes } from '@/routes'

import s from './post-card.module.less'

interface PostCardMenuProps {
  post: RequestedPostFragment
  isGroup: boolean
  isUser: boolean
  isSection: boolean
  isTop: boolean
  isRecommended: boolean
}

export const PostCardMenu: React.FC<PostCardMenuProps> = ({
  post,
  isGroup,
  isUser,
  isSection,
  isTop,
  isRecommended,
}) => {
  const [t] = useTranslation()

  const PostTag = {
    [PostTagType.WorkProcess]: { label: t('post.workTag'), color: 'red' },
    [PostTagType.TechShare]: { label: t('post.techTag'), color: 'blue' },
    [PostTagType.Entertainment]: { label: t('post.lifeTag'), color: 'green' },
  }

  const history = useHistory()

  const [visible, setVisible] = React.useState(false)
  const [menuVisible, { toggle: menuToggle }] = useToggle()
  const [archivePost, unarchivePost] = useArchivePost(post.postId)
  const [featurePost, unFeaturePost] = useFeaturePost(post.postId)
  const [topFeatured, unTopFeatured] = useTopFeatured(post.postId)
  const [topPost, untopPost] = useTopPost(post, isGroup, isSection)
  const [toggleFavorPost] = usePostFavorToggle(post.postId)

  const [tagUpdate] = usePostTagUpdateMutation({
    onCompleted() {
      message.success(t('post.tagUpdateSuccess'))
    },
    onError() {
      message.error(t('post.tagUpdateFail'))
    },
  })

  const isNewsFeed = !isGroup && !isUser && !isSection
  const isArchived = !!post.archivedAt
  const isFeatured = !!post.featuredAt

  const { isGroupAdmin } = useIsGroupAdmin(post.group as Group)
  const isSystemAdmin = useIsSystemAdmin()

  const pageUrl = routes.posts.page(post.postId)
  const openButton = !isArchived && (
    <Button
      className={s.dropdownButton}
      shape={'circle'}
      icon={
        <Tooltip title={t('post.enterPost')}>
          <SwapRightOutlined className={s.actionButton} />
        </Tooltip>
      }
      onClick={() => history.push(pageUrl)}
    />
  )

  const linkButton = !isArchived && !isRecommended && (
    <Button
      className={s.dropdownButton}
      shape={'circle'}
      icon={
        <Tooltip title={t('post.copyLink')}>
          <LinkOutlined className={s.actionButton} />
        </Tooltip>
      }
      onClick={() => {
        copy(window.location.origin + routes.posts.page(post.postId))
        message.success(t('post.copySuccess'))
      }}
    />
  )

  const favorButton = !isArchived && !isRecommended && (
    <Button
      className={s.dropdownButton}
      shape={'circle'}
      icon={
        <Tooltip title={post.isFavored ? t('post.unSavePost') : t('post.savePost')}>
          {post.isFavored ? <StarFilled className={s.actionButton} /> : <StarOutlined className={s.actionButton} />}
        </Tooltip>
      }
      onClick={() => toggleFavorPost()}
    />
  )

  const tagMenu = (
    <Menu
      onClick={({ key }) =>
        tagUpdate({
          variables: {
            postId: post.postId,
            tags: [key as PostTagType],
          },
        })
      }
    >
      <Menu.ItemGroup title={t('post.contentLabel')}>
        {Object.keys(PostTag).map((key) => (
          <Menu.Item key={key}>{PostTag[key].label}</Menu.Item>
        ))}
      </Menu.ItemGroup>
    </Menu>
  )

  const tagDropdown = isSystemAdmin && (
    <Dropdown overlay={tagMenu}>
      <Button className={s.dropdownButton} shape='circle' icon={<TagsOutlined className={s.actionButton} />} />
    </Dropdown>
  )
  const tags =
    isSystemAdmin &&
    post.tags.map((tag) => (
      <Tag style={{ marginRight: 0 }} key={tag} color={PostTag[tag].color}>
        {PostTag[tag].label}
      </Tag>
    ))

  const menuLink = !isArchived && isRecommended && (
    <Menu.Item
      key='copy'
      icon={<LinkOutlined />}
      onClick={() => {
        copy(window.location.origin + routes.posts.page(post.postId))
        message.success(t('post.copySuccess'))
      }}
    >
      {t('post.copyLink')}
    </Menu.Item>
  )

  const menuFavor = !isArchived && isRecommended && (
    <Menu.Item key='favor' icon={post.isFavored ? <StarFilled /> : <StarOutlined />} onClick={() => toggleFavorPost()}>
      {post.isFavored ? t('post.unSavePost') : t('post.savePost')}
    </Menu.Item>
  )

  const EditIcon = <span>{t('post.edit')}</span>

  const showEdit =
    (post.isViewerCreated || isGroupAdmin || isSystemAdmin) && !isArchived && !(post.postType === PostType.Introduction)

  const menuEditInner = (
    <Menu.Item key='edit' icon={<EditOutlined />}>
      {EditIcon}
    </Menu.Item>
  )

  const menuEdit = (() => {
    switch (post.postType!) {
      case 'Thanks':
        return (
          <ThanksMessageModal post={post} receiver={post.creator!}>
            {menuEditInner}
          </ThanksMessageModal>
        )
      case 'Event':
        return <EventPostModal post={post}>{menuEditInner}</EventPostModal>
      case 'Announcement':
        return <AnnouncementPostModal post={post}>{menuEditInner}</AnnouncementPostModal>
      case 'Honor':
        return <HonorPostModal post={post}>{menuEditInner}</HonorPostModal>
      default:
        return React.cloneElement(menuEditInner, {
          onClick: () => setVisible(true),
        })
    }
  })()

  const showTop = !isNewsFeed && ((isGroup && isGroupAdmin) || (isSection && isSystemAdmin))

  const menuTop = (
    <Menu.Item
      key='top'
      icon={isTop ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
      onClick={() => (isTop ? untopPost() : topPost())}
    >
      {isTop ? t('post.isTop') : t('post.notTop')}
    </Menu.Item>
  )

  const archiveOnConfirm = () => {
    menuToggle(false)
    if (isTop) {
      untopPost()
    }
    isArchived ? unarchivePost() : archivePost()
  }

  const showArchive = post.isViewerCreated || isGroupAdmin || isSystemAdmin
  const menuArchive = (
    <Menu.Item key='archive' icon={<DeleteOutlined />} onClick={() => menuToggle(true)}>
      <Popconfirm
        title={`你确定要${isArchived ? '取消' : ''}隐藏此帖子吗？`}
        placement='bottom'
        onCancel={() => menuToggle(false)}
        onConfirm={archiveOnConfirm}
      >
        <Tooltip title={isArchived ? t('post.unArchiveTooltip') : t('post.archiveTooltip')}>
          {isArchived ? t('post.unArchive') : t('post.archive')}
        </Tooltip>
      </Popconfirm>
    </Menu.Item>
  )

  const showFeature = isSystemAdmin && !isArchived
  const menuFeature = (
    <>
      <Menu.Divider />
      <Menu.Item key='feature' icon={<RocketOutlined />} onClick={() => (isFeatured ? unFeaturePost() : featurePost())}>
        <Tooltip title={isFeatured ? t('post.unFeatureTooltip') : t('post.featuredTooltip')}>
          {isFeatured ? t('post.unFeature') : t('post.feature')}
        </Tooltip>
      </Menu.Item>
      {isFeatured && (
        <>
          <Menu.Item
            key='featureTop'
            icon={post.isFeaturedTop ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
            onClick={() => (post.isFeaturedTop ? unTopFeatured() : topFeatured())}
          >
            <Tooltip title={post.isFeaturedTop ? t('post.unTopFeaturedtips') : t('post.topFeaturedtips')}>
              {post.isFeaturedTop ? t('post.unTopFeatured') : t('post.topFeatured')}
            </Tooltip>
          </Menu.Item>
        </>
      )}
    </>
  )

  const dropdownMenu = (
    <Menu onMouseLeave={() => menuToggle(false)}>
      {menuLink}
      {menuFavor}
      {showEdit && menuEdit}
      {showArchive && menuArchive}
      {showTop && menuTop}
      {showFeature && menuFeature}
    </Menu>
  )

  const showMoreActions = post.isViewerCreated || isGroupAdmin || isSystemAdmin
  const moreActions = (
    <Dropdown {...(menuVisible && { visible: menuVisible })} trigger={['click']} overlay={dropdownMenu}>
      <Button className={s.dropdownButton} shape='circle' icon={<EllipsisOutlined className={s.actionButton} />} />
    </Dropdown>
  )

  return (
    <>
      <div className={s.postCardMenu}>
        <span className={s.postActions}>
          {tags}
          {tagDropdown}
          {openButton}
          {linkButton}
        </span>
        {favorButton}
        {showMoreActions && moreActions}
      </div>
      <PostEditor
        visible={visible}
        groupId={post.group?.groupId}
        postType={post.postType || undefined}
        onClose={() => setVisible(false)}
        post={post}
      />
    </>
  )
}
